import { useEffect, useState } from "react";
import { FileText, Minus, Package, Plus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, fmtDate, money } from "@/lib/api";
import { PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { P, StatusPill, openPdf } from "@/components/studio/portalKit";
import { MyRolls } from "@/components/studio/MyRolls";

const ProductCard = ({ p, qty, setQty }) => (
  <Card data-testid={`product-${p.id}`} className="overflow-hidden transition-shadow hover:shadow-md">
    <div className="flex h-28 items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
      {p.image_url ? <img src={p.image_url} alt={p.name} className="h-full w-full object-cover" />
        : <Package className="h-10 w-10 text-brand-deep/40" strokeWidth={1.4} />}
    </div>
    <CardContent className="space-y-3 p-4">
      <div>
        <p className="font-semibold leading-tight text-brand-ink">{p.name}</p>
        <p className="text-xs text-slate-500">{p.category} · per {p.unit}</p>
      </div>
      <div className="flex items-center gap-2">
        <Button size="icon" variant="outline" className="h-9 w-9" data-testid={`qty-minus-${p.id}`}
          onClick={() => setQty(Math.max(0, (qty || 0) - 1))}><Minus className="h-4 w-4" /></Button>
        <Input data-testid={`qty-input-${p.id}`} type="number" min="0" value={qty || ""} placeholder="0"
          onChange={(e) => setQty(Math.max(0, Number(e.target.value)))} className="h-9 text-center" />
        <Button size="icon" variant="outline" className="h-9 w-9" data-testid={`qty-plus-${p.id}`}
          onClick={() => setQty((qty || 0) + 1)}><Plus className="h-4 w-4" /></Button>
      </div>
    </CardContent>
  </Card>
);

const OrderRow = ({ o }) => (
  <Card data-testid={`my-order-${o.id}`}>
    <CardContent className="space-y-2 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="font-semibold text-brand-ink">{o.order_no} <span className="text-xs font-normal text-slate-500">· {fmtDate(o.created_at)}</span></p>
        <StatusPill status={o.status} testid={`my-order-status-${o.id}`} />
      </div>
      <p className="text-sm text-slate-600">{o.items.map((i) => `${i.quantity} ${i.unit} ${i.name}`).join(" · ")}</p>
      {o.note && <p className="text-xs text-slate-500">Note: {o.note}</p>}
      {o.cancel_reason && <p className="text-xs text-red-600">Cancelled: {o.cancel_reason}</p>}
      {o.status === "Dispatched" && (
        <p className="text-xs text-slate-600">{o.courier_name}{o.lr_number && ` · LR ${o.lr_number}`}{o.vehicle_details && ` · ${o.vehicle_details}`}
          {o.serials.length > 0 && ` · Rolls: ${o.serials.map((s) => s.serial).join(", ")}`}</p>
      )}
      {o.invoice_available && (
        <div className="flex items-center justify-between rounded-md bg-emerald-50 px-3 py-2">
          <span data-testid={`invoice-amount-${o.id}`} className="text-sm font-semibold text-emerald-800">Invoice {money(o.invoice_amount)}</span>
          <Button size="sm" variant="outline" data-testid={`invoice-pdf-${o.id}`} onClick={() => openPdf(`${P}/orders/${o.id}/invoice.pdf`)}>
            <FileText className="mr-1.5 h-3.5 w-3.5" /> Invoice PDF
          </Button>
        </div>
      )}
    </CardContent>
  </Card>
);

export default function StudioOrders() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [cart, setCart] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("new");
  const load = () => api.get(`${P}/orders`).then(({ data }) => setOrders(data));
  useEffect(() => {
    api.get(`${P}/products`).then(({ data }) => setProducts(data));
    load();
  }, []);
  const lines = Object.entries(cart).filter(([, q]) => q > 0);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`${P}/orders`, { items: lines.map(([sku_id, quantity]) => ({ sku_id, quantity })), note });
      toast.success("Order placed. VeloGuardX will send your proforma on WhatsApp.");
      setCart({}); setNote(""); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="space-y-6">
      <PageHead title="Material Order" subtitle="Pick quantities and submit. Orders are prepaid — we dispatch once payment is received." />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="new" data-testid="tab-new-order">New order</TabsTrigger>
          <TabsTrigger value="mine" data-testid="tab-my-orders">My Orders ({orders.length})</TabsTrigger>
          <TabsTrigger value="rolls" data-testid="tab-my-rolls">My Rolls</TabsTrigger>
        </TabsList>
        <TabsContent value="rolls"><MyRolls onReorder={() => setTab("new")} /></TabsContent>
        <TabsContent value="new" className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {products.map((p) => <ProductCard key={p.id} p={p} qty={cart[p.id]} setQty={(q) => setCart({ ...cart, [p.id]: q })} />)}
          </div>
          <Card className="sticky bottom-4 border-brand-deep/20 shadow-lg">
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-end">
              <Textarea data-testid="order-note" placeholder="Note for VeloGuardX (optional)" value={note}
                onChange={(e) => setNote(e.target.value)} className="min-h-[44px] flex-1" />
              <Button data-testid="submit-order-btn" disabled={!lines.length || busy} onClick={submit}
                className="min-h-[44px] bg-crimson-500 hover:bg-crimson-600">
                <ShoppingCart className="mr-2 h-4 w-4" /> Submit order ({lines.length} item{lines.length === 1 ? "" : "s"})
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="mine" className="space-y-3">
          {orders.length === 0 && <p className="text-sm text-slate-500">No orders yet.</p>}
          {orders.map((o) => <OrderRow key={o.id} o={o} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
